// src/pages/CertificateNamePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Application configuration
const APPLICATIONS = {
  PART1: {
    id: "PART1",
    name: "MISSING: The Search for Terry Hughes",
    shortName: "MISSING Part I"
  },
  PART2: {
    id: "PART2",
    name: "MISSING: The Sequel",
    shortName: "MISSING Part II"
  }
};

function CertificateNamePage() {
  const [displayName, setDisplayName] = useState("");
  const [status, setStatus] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [token, setToken] = useState("");
  const [application, setApplication] = useState("PART1"); // Default to Part I
  const [tokenValid, setTokenValid] = useState(false);
  const [validating, setValidating] = useState(true);
  const [succeeded, setSucceeded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only run verification if we're not already in a success state
    if (!succeeded) {
      // Get email, token, and application from URL
      const params = new URLSearchParams(location.search);
      const emailParam = params.get('email');
      const tokenParam = params.get('token');
      const appParam = params.get('application');
      
      // Set application if provided, otherwise default to PART1
      if (appParam && APPLICATIONS[appParam]) {
        setApplication(appParam);
      }
      
      if (emailParam && tokenParam) {
        setUserEmail(emailParam);
        setToken(tokenParam);
        
        // Verify the token
        verifyToken(emailParam, tokenParam);
      } else {
        setValidating(false);
      }
    }
  }, [location, succeeded]);

  const verifyToken = async (email, token) => {
    try {
      const response = await fetch("https://admin.cretecinc.com/verify-certificate-token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ email, token })
      });
      
      const data = await response.json();
      
      if (response.ok && data.valid) {
        setTokenValid(true);
        
        // Update application if returned from the token verification
        if (data.application && APPLICATIONS[data.application]) {
          setApplication(data.application);
        }
      } else {
        setStatus(`Error: ${data.error || "Invalid certificate link"}`);
      }
    } catch (error) {
      console.error("Error verifying token:", error);
      setStatus("Error: Could not verify certificate link.");
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("Submitting...");

    try {
      const response = await fetch("https://admin.cretecinc.com/unauthenticated/generate-certificate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          userEmail,
          displayName,
          token,
          application
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || "Failed to generate certificate");
      }

      // Mark success state immediately to prevent further token validation
      setSucceeded(true);
      setStatus("Certificate is on its way! Check your email soon.");
      setDisplayName(""); // clear the field
    } catch (error) {
      console.error("Error generating certificate:", error);
      setStatus(`Error: ${error.message || "Could not generate certificate."}`);
    }
  };

  // Get the current application name
  const getApplicationName = () => {
    return APPLICATIONS[application]?.name || "MISSING";
  };

  // Get the current application short name
  const getApplicationShortName = () => {
    return APPLICATIONS[application]?.shortName || "MISSING";
  };

  // If we've successfully submitted, show only the success message
  if (succeeded) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '1em' }}>
        <h2>Certificate Request Successful</h2>
        <p>Your certificate has been generated and is on its way to your email.</p>
        <p>Thank you for completing {getApplicationName()}!</p>
        <button onClick={() => navigate('/')}>Return to Login</button>
      </div>
    );
  }

  if (validating) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '1em' }}>
        <h2>Verifying Certificate Link</h2>
        <p>Please wait while we verify your certificate link...</p>
      </div>
    );
  }

  // If no email or token found, or token invalid
  if (!userEmail || !token || !tokenValid) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '1em' }}>
        <h2>Invalid Certificate Link</h2>
        <p>This certificate link is invalid, has expired, or has already been used.</p>
        {status && <p style={{ color: 'red' }}>{status}</p>}
        <button onClick={() => navigate('/')}>Go to Login</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '1em' }}>
      <h2>Claim Your {getApplicationShortName()} Completion Certificate</h2>
      <p>Please enter the name you want displayed on your certificate.</p>
      <p>Certificate will be sent to: <strong>{userEmail}</strong></p>
      <p>Application: <strong>{getApplicationName()}</strong></p>

      <form onSubmit={handleSubmit}>
        <label htmlFor="displayName">Your Name:</label>
        <input
          type="text"
          id="displayName"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={30}
          required
          style={{ width: '100%', marginBottom: '1em' }}
        />
        <button type="submit">Submit</button>
      </form>

      {status && <p>{status}</p>}
    </div>
  );
}

export default CertificateNamePage;